import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { useRouter } from 'next/router';
import useSWR, { KeyLoader } from 'swr';
import _, { includes, first, get } from 'lodash';
import querystring from 'query-string';
import useSWRInfinite from 'swr/infinite';
import { shopDetailGateway } from '@/di-container/shop/detail/gateway/client';
import { constructionCaseGateway } from '@/di-container/construction-case/gateway/client';
import {
  ConstructionCaseContent,
  GetConstructionCaseListRequest,
  GetConstructionCaseListResponse,
} from '@remodeling/content';
import { ApiPagingResponseData } from '@remodeling/base';
import { scrapGateway } from '@/di-container/common/scrap/gateway/client';
import { useModal } from '@/providers/ModalProvider/ModalProvider';
import { authGateway } from '@/di-container/auth/gateway/client';
import { usePopup } from '@/providers/PopupProvider/PopupProvider';
import { useBraze } from '@/providers/BrazeProvider';
import { AgentsWithAgency } from '@remodeling/shop';
import ConstructionPopup from '../ui/ConstructionPopup';
import { ApartRanks, Consultants, ShopDetailViewInfo } from '@/types/shop';
import useAuthenticated from '@/hooks/useAuthenticated';
import {
  SHOP_DETAIL_VIEW_TYPE_DESIGNPARK,
  SHOP_DETAIL_VIEW_TYPE_INTERIOR,
  SHOP_DETAIL_VIEW_TYPE_RHKB,
  SHOP_DETAIL_VIEW_TYPE_SHOWROOM,
  SOUTH_KOREA_LEVEL_MAX,
} from '@/constants/shop';
import { NewTextButton, useToast } from '@design-system/pc';
import { TOAST_Z_INDEX } from '@/styles';

export interface UseShopDetail {
  shopId: number;
}

const useShopDetail = ({ shopId }: UseShopDetail) => {
  const [level, setLevel] = useState<number>(4);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<{
    detailDataState: boolean;
    constructDataState: boolean;
  }>({
    detailDataState: false,
    constructDataState: false,
  });
  const store = useStore();
  const [apartRankList, setApartRankList] = useState<ApartRanks[] | undefined>(
    undefined,
  );
  const [isScrap, setIsScrap] = useState<boolean>(false);
  const [tabState, setTabState] = useState<{
    code: string;
    name: string;
    index: number;
  }>({
    code: '',
    name: '',
    index: 0,
  });
  const { logCustomEvent } = useBraze();

  const { createScraps, deleteScraps } = scrapGateway();
  const { isAuthenticated } = useAuthenticated();
  const { loginGateway } = authGateway(store.dispatch);
  const { handleOpen: modalOpen } = useModal();
  const { handleOpen: popupOpen } = usePopup();
  const { toast } = useToast();
  const { data: apiResponse } = useSWR({ shopId }, () => {
    return shopDetailGateway().getDetail.exec({
      shopId,
      typeValue: 'PC',
    });
  });

  const { data: rdApiResponse } = useSWR(
    apiResponse ? `re-list-${shopId}` : null,
    () => {
      const agencyType = includes(
        ['S01', 'S02', 'S03', 'S04'],
        apiResponse?.data?.type?.code,
      )
        ? '02'
        : '01';

      return shopDetailGateway().getAgents.exec({
        agencyCode: apiResponse?.data?.shopCode || '',
        agencyType,
      });
    },
  );

  const getKey: KeyLoader = () => {
    const query = querystring.stringify({
      page: 1,
      size: 30,
      shopId: String(shopId),
      exhibitHall: tabState.code,
      sort: 'R',
      typeValue: 'P',
    });
    return `shop_detail_${query}`;
  };

  const { data: constructData } =
    useSWRInfinite<GetConstructionCaseListResponse>(getKey, (query) => {
      setIsLoading({ ...isLoading, constructDataState: false });
      return constructionCaseGateway().getList.exec(
        querystring.parse(query) as unknown as GetConstructionCaseListRequest,
      );
    });

  const handleLevel = (num: number) => {
    if (num >= SOUTH_KOREA_LEVEL_MAX) {
      setLevel(SOUTH_KOREA_LEVEL_MAX);
    } else if (num === 1) {
      setLevel(1);
    } else {
      setLevel(num);
    }
  };

  const handleGetApartRank = useCallback(async () => {
    const res = await shopDetailGateway().getApartRanksList.exec({
      shopId,
    });
    if (res.code === 200) {
      setApartRankList(res.data?.ranks);
    }
  }, [shopId]);

  // quickReserveYn 타입 추가 필요
  const shopDetail: any = useMemo(() => {
    if (apiResponse?.data) {
      setIsLoading({ ...isLoading, detailDataState: true });
      const {
        id,
        name,
        type,
        telNumber,
        vrView,
        address,
        amenities,
        mainImages,
        includeItems,
        operateTime,
        floorGuides,
        exhibitHalls,
        scrapYn,
        badges,
        reserveVisitYn,
        quickReserveYn,
      } = apiResponse.data;

      let rdConsultants: Consultants[] | [] = [];

      if (rdApiResponse && rdApiResponse.data) {
        rdConsultants =
          rdApiResponse.data?.map((data: AgentsWithAgency) => {
            return {
              id: Number(data.agentDetail.code) || 0,
              code: data.agentDetail.code || '',
              name: `${data.agentDetail.name} RD` || '',
              description: data.agentDetail.introduction || '',
              image: {
                url: data.agentDetail.image?.url || '',
              },
              shop: {
                id: data.agency.id,
                name: data.agency.name,
                type: {
                  name: data.agency.type?.name || '',
                  code: data.agency.type?.code || '',
                  groupCode: '',
                },
              },
              isRD: true,
            };
          }) || [];
      }

      const translateOperateTime = (operateTime?: string) => {
        const splitedOperateTime = operateTime?.split(': ');
        return {
          day: splitedOperateTime && splitedOperateTime[0] + ':',
          time: splitedOperateTime && splitedOperateTime[1],
        };
      };

      const shopDetail: ShopDetailViewInfo = {
        shopId: id,
        header: {
          name,
          type,
          telNumber:
            telNumber?.telYn === 'Y' &&
            telNumber?.number.length > 6 &&
            telNumber?.number.indexOf('000-0000') === -1
              ? { ...telNumber }
              : {
                  telYn: 'N',
                  number: '',
                },
          vrView,
          address,
          amenities: amenities ? `편의시설: ${amenities}` : '',
          mainImages,
          includeItem: includeItems?.reduce((prev, curr, index) => {
            const newName =
              index !== includeItems.length - 1 ? curr.name + ', ' : curr.name;
            return prev + newName;
          }, ''),
          operateTime: {
            weekday: translateOperateTime(operateTime?.weekday),
            weekend: translateOperateTime(operateTime?.weekend),
            closed: translateOperateTime(operateTime?.closed),
          },
          floorGuides: floorGuides.map((floorGuide) => ({
            floor: floorGuide.floor ? floorGuide.floor + '층' : '',
            description: floorGuide.description
              .split(',')
              .map((word) => _.trim(word))
              .join(', '),
          })),
          badges,
          description: exhibitHalls?.[0]?.intro.description || '',
        },
        bottomBar: {
          id,
          isScrap: scrapYn === 'Y',
          imageUrl: mainImages?.[0]?.url,
          name,
          type,
          reserveVisitYn: reserveVisitYn === 'Y',
          quickReserveYn: quickReserveYn === 'Y',
        },
      };

      switch (type.code) {
        case 'S01':
        case 'S02':
          shopDetail.exhibitHallList = exhibitHalls?.map((exhibitHall) => {
            return {
              ...exhibitHall,
              consultants:
                exhibitHall.code === 'RM' && rdConsultants
                  ? rdConsultants
                  : exhibitHall.code === 'KCBR'
                  ? []
                  : exhibitHall.consultants,
              name: `${exhibitHall.name}관`,
              id,
            };
          });
          break;
        case 'S03':
          shopDetail.exhibitHallList = exhibitHalls?.map((exhibitHall) => {
            return {
              ...exhibitHall,
              consultants:
                exhibitHall.code === 'KCBR' && rdConsultants
                  ? rdConsultants
                  : [],
              name: `${exhibitHall.name}관`,
              id,
            };
          });
          break;
        case 'S04':
          shopDetail.exhibitHallList = exhibitHalls?.map((exhibitHall) => {
            return { ...exhibitHall, name: `${exhibitHall.name}관`, id };
          });
          break;
        case 'S05':
        case 'S06':
          shopDetail.agentRhkb = {
            id,
            code: get(first(exhibitHalls), 'code', 'RM'),
            images: get(first(exhibitHalls), 'images', []),
            consultants: rdConsultants,
            badges: get(first(exhibitHalls), 'badges', []).map((badge) => {
              return {
                ...badge,
                description: badge.description.split('\\n'),
              };
            }),
            apartRanks: get(first(exhibitHalls), 'apartRanks', []),
            consultantShowYn: get(first(exhibitHalls), 'consultantShowYn', 'N'),
          };
          break;
        default:
          shopDetail.agentInterior = {
            id,
            code: get(first(exhibitHalls), 'code', 'RM'),
            images: get(first(exhibitHalls), 'images', []),
            consultants: get(first(exhibitHalls), 'consultants', []),
            consultantShowYn: get(first(exhibitHalls), 'consultantShowYn', 'N'),
          };
          break;
      }

      switch (type.code) {
        case 'S01':
          shopDetail.header!.tooltipTitle = `한샘 ${name}?`;
          shopDetail.header!.tooltipDescription =
            '홈 리모델링부터 가구, 생활용품까지 집 꾸밈의 모든 것을 한번에 만나볼 수 있는 한샘의 프리미엄 직영매장';

          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_DESIGNPARK;
          break;
        case 'S02':
          shopDetail.header!.tooltipTitle = '한샘리하우스란?';
          shopDetail.header!.tooltipDescription =
            '자재 선택부터 컨설팅까지 홈 리모델링에 대한 원스톱 서비스를 제공하는 토탈 홈 리모델링 전문 매장';

          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_SHOWROOM;
          break;
        case 'S03':
          shopDetail.header!.tooltipTitle = '한샘키친&바스란?';
          shopDetail.header!.tooltipDescription =
            '매월 1만건 이상의 설계와 시공으로 국내 최고 수준의 전문성을 지닌 부엌 & 욕실 프리미엄 매장';

          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_SHOWROOM;
          break;
        case 'S04':
        case 'S05':
          shopDetail.header!.tooltipTitle = '리하우스 대리점이란?';
          shopDetail.header!.tooltipDescription =
            '홈 리모델링에 대한 원스톱 서비스를 제공하는 한샘 공식 대리점';

          shopDetail.viewType =
            type.code === 'S04'
              ? SHOP_DETAIL_VIEW_TYPE_SHOWROOM
              : SHOP_DETAIL_VIEW_TYPE_RHKB;
          break;
        case 'S06':
          shopDetail.header!.tooltipTitle = '키친&바스 대리점이란?';
          shopDetail.header!.tooltipDescription =
            '부엌 & 욕실 공사에 대한 원스톱 서비스를 제공하는 한샘 공식 대리점';

          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_RHKB;
          break;
        case 'S07':
          shopDetail.header!.tooltipTitle = '홈퍼니싱 대리점이란?';
          shopDetail.header!.tooltipDescription =
            '한샘의 침대, 소파, 책상 등 인테리어 가구를 만나볼 수 있는 공식 대리점';

          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_INTERIOR;
          break;
        case 'S11':
          shopDetail.header!.tooltipTitle = '보여주는 집이란?';
          shopDetail.header!.tooltipDescription =
            '한샘으로 시공된 주거 현장에 고객이 직접 방문하여 체험 할 수 있는 고객 체험형 공간';

          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_INTERIOR;
          break;
        default:
          shopDetail.viewType = SHOP_DETAIL_VIEW_TYPE_INTERIOR;
          break;
      }
      setTabState({
        code: shopDetail?.exhibitHallList?.[0]?.code || '',
        name: shopDetail?.exhibitHallList?.[0]?.name || '',
        index: 0,
      });

      return shopDetail;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse, apartRankList, rdApiResponse]);

  const constructList:
    | ApiPagingResponseData<ConstructionCaseContent>
    | undefined = useMemo(() => {
    if (constructData?.[0].data) {
      setIsLoading({ ...isLoading, constructDataState: true });
      const { data } = constructData[0];
      return data;
    } else {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constructData]);

  const handleDeleteScraps = useCallback(async () => {
    const { code, message } = await deleteScraps.exec({
      scrapTypeCode: 'SHOP',
      targetId: shopId,
    });

    if (code !== 200 && message) {
      return true;
    }

    return false;
  }, [deleteScraps, shopId]);

  const handleConstructionPopup = () => {
    popupOpen({
      popupProps: {
        buttonType: 'noneBtn',
        scrollControl: false,
        title: `${tabState.name} 시공사례`,
        popupSize: 'xxl',
        children: (
          <ConstructionPopup shopId={shopId} tabState={tabState.code} />
        ),
      },
    });
  };

  const handleGoScrap = useCallback(() => {
    router.push(
      `${process.env.NEXT_PUBLIC_HANSSEMMALL_HOST}/mypage/wishList.do`,
    );
  }, [router]);

  const handleCreateScraps = useCallback(async () => {
    const { code, message } = await createScraps.exec({
      scrapTypeCode: 'SHOP',
      target: {
        id: shopId,
        name: shopDetail?.header.name,
        landingUrl: window.location.href,
        imageUrl: shopDetail?.header.mainImages[0].url,
      },
    });

    if (code !== 200 && message) {
      return false;
    }

    toast.success('찜 목록에 추가하였습니다.', {
      placement: 'bottom',
      variant: 'button',
      actionButton: (
        <NewTextButton
          size="lg"
          isUnderline
          color="color_chip.white"
          onClick={handleGoScrap}
          width={74}
        >
          찜 목록가기
        </NewTextButton>
      ),
      offset: 87,
      zIndex: TOAST_Z_INDEX,
    });

    return true;
  }, [
    createScraps,
    handleGoScrap,
    shopDetail?.header.mainImages,
    shopDetail?.header.name,
    shopId,
    toast,
  ]);

  const handleScrap = useCallback(async () => {
    if (!isAuthenticated) {
      modalOpen({
        modalProps: {
          type: 'confirm',
          text: `로그인 후 이용 가능합니다.\n로그인 하시겠습니까?`,
          gtmName: 'login_modal',
          onConfirm: async () => {
            await loginGateway.exec();
          },
        },
      });

      return;
    }

    if (isScrap) {
      setIsScrap(false);
      const scrapResult = await handleDeleteScraps();
      setIsScrap(scrapResult);
    } else {
      setIsScrap(true);
      const scrapResult = await handleCreateScraps();
      setIsScrap(scrapResult);

      logCustomEvent({
        eventName: 'bz_add_scrap_store',
        eventProperties: {
          bz_store_type: shopDetail?.bottomBar?.type?.name,
          bz_store_area: shopDetail?.header?.address?.basic,
          bz_store_num: _.toString(shopDetail?.shopId),
          bz_store_name: shopDetail?.header?.name,
        },
      });
    }
  }, [
    isAuthenticated,
    isScrap,
    modalOpen,
    loginGateway,
    handleDeleteScraps,
    handleCreateScraps,
    logCustomEvent,
    shopDetail?.bottomBar?.type?.name,
    shopDetail?.header?.address?.basic,
    shopDetail?.header?.name,
    shopDetail?.shopId,
  ]);

  // OP04_01(리하우스대리점) | OP04_02(디자인파크)
  const handleConsult = useCallback(
    (type: 'visit' | 'quick') => {
      switch (type) {
        case 'quick': {
          router.push({
            pathname: '/quick-consult/request',
            query: {
              channelType: shopDetail.viewType > 2 ? 'OP04_01' : 'OP04_02',
              channelId: shopId,
              channelUrl: window.location.href,
            },
          });
          break;
        }
        case 'visit': {
          router.push({
            pathname: '/visit-consult/request',
            query: {
              shopId,
              channelType: 'OP20_01',
              channelUrl: window.location.href,
            },
          });
          break;
        }
      }
    },
    [router, shopId, shopDetail],
  );

  const handleLinkOpen = useCallback(
    (link: string) => {
      router.push(link);
    },
    [router],
  );

  useEffect(() => {
    if (shopDetail) {
      setIsScrap(shopDetail.bottomBar.isScrap);
    }
  }, [shopDetail]);

  useEffect(() => {
    handleGetApartRank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    level,
    isScrap,
    handleScrap,
    shopDetail,
    isLoading,
    constructList,
    handleConsult,
    handleLinkOpen,
    apartRankList,
    handleConstructionPopup,
    tabState,
    setTabState,
    handleLevel,
  };
};

export default useShopDetail;
